import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../api/core';

const DEBOUNCE_MS = 500;

/**
 * Fetches server-rendered HTML for draft components via POST and returns
 * the raw HTML string for use with iframe `srcdoc`.
 *
 * Using srcdoc (instead of a Blob URL) ensures the iframe inherits the
 * parent page's origin, which prevents YouTube Error 153 caused by
 * opaque/null origins on blob: iframes.
 */
export function useBackendPreview(pageId: string, components: any[] | undefined, isOpen: boolean) {
  const [html, setHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHashRef = useRef<string>('');

  const fetchPreview = useCallback(
    async (comps: any[]) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('authToken');
        const payload = JSON.stringify({
          components: JSON.stringify({ components: comps }),
        });

        const res = await fetch(`${API_BASE_URL}/pages/${pageId}/preview-html`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: payload,
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Preview failed (${res.status})`);
        }

        const text = await res.text();
        setHtml(text);
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        console.error('Preview fetch error:', err);
        setError(err.message || 'Failed to load preview');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [pageId]
  );

  useEffect(() => {
    if (!isOpen || !components) return;

    const hash = JSON.stringify(components);
    if (hash === lastHashRef.current && html) return;
    lastHashRef.current = hash;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchPreview(components);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // html intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components, isOpen, fetchPreview]);

  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setHtml(null);
      lastHashRef.current = '';
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const refresh = useCallback(() => {
    if (components) {
      lastHashRef.current = '';
      fetchPreview(components);
    }
  }, [components, fetchPreview]);

  return { html, isLoading, error, refresh };
}
